<template>
  <form @submit.prevent="onSubmit">
    <div class="flex items-baseline mb-2">
      <label
        for="displayname"
        class="block px-0.5 mb-1 text-sm font-medium select-none text-gray-800/70"
        >Display Name
      </label>
      <fa-icon
        v-if="!hideCancel && !showEdit"
        icon="pencil"
        class="ml-2 text-xs transition-colors cursor-pointer text-gray-800/70 hover:text-gray-800"
        title="Edit display name"
        @click.prevent="toggleEditMode" />
      <span
        v-if="!hideCancel && showEdit"
        class="ml-2 text-xs cursor-pointer select-none hover:underline text-gray-800/70"
        @click.prevent="toggleEditMode">
        cancel
      </span>
    </div>

    <div class="flex items-center space-x-2">
      <div
        class="flex overflow-hidden flex-1 items-center px-1 space-x-2 rounded-md border-0 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        :class="
          showEdit ? 'bg-white/40 text-black' : 'bg-white/40 text-blue-500'
        ">
        <input
          id="displayname"
          v-model="displayname"
          type="text"
          autocomplete="displayname"
          placeholder="(None)"
          :readonly="!showEdit"
          :disabled="!showEdit"
          class="block w-full text-sm font-bold bg-transparent border-none ring-0 placeholder:text-gray-300" />
        <template v-if="showEdit && !autoEdit">
          <button
            v-if="!formBusy"
            type="submit"
            class="px-4 w-20 h-7 font-bold text-white bg-indigo-600 rounded-md cursor-pointer text-2xs hover:bg-indigo-500"
            @click.prevent="onSubmit">
            Save
          </button>
          <button
            v-else
            class="px-4 w-20 h-7 text-xs font-bold text-white bg-gray-400 rounded-md cursor-not-allowed">
            <fa-icon icon="spinner-third" spin />
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="formError"
      class="p-2 mt-2 text-xs text-red-500 rounded-md cursor-pointer select-none bg-white/50 hover:bg-white/70"
      @click.prevent="formError = null">
      <fa-icon icon="triangle-exclamation" /> Failed to update displayname
      <p class="font-bold">
        <span v-if="formError.message">{{ formError.message }}</span>
        <span v-else>{{ formError }}</span>
      </p>
    </div>
  </form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStoreProfile } from '@/stores/storeProfile'

const storeProfile = useStoreProfile()
const props = defineProps({
  profile: Object,
  autoEdit: {
    type: Boolean,
    default: false
  },
  hideCancel: {
    type: Boolean,
    default: false
  }
})

const showEdit = ref(false)
const displayname = ref('')
const formBusy = ref(false)
const formError = ref(null)

onMounted(() => {
  displayname.value = props.profile?.displayname
  // Auto-enable edit mode if autoEdit prop is true
  if (props.autoEdit) {
    showEdit.value = true
  }
})

const toggleEditMode = () => {
  if (showEdit.value) {
    cancelEditMode()
  } else {
    showEdit.value = !showEdit.value
    formError.value = null
  }
}

const cancelEditMode = () => {
  showEdit.value = false
  formError.value = null
  displayname.value = props.profile?.displayname
}

const updateDisplayname = () => {
  formBusy.value = true
  storeProfile
    .updateProfile({ displayname: displayname.value })
    .then(() => {
      formBusy.value = false
      toggleEditMode()
    })
    .catch((error) => {
      formBusy.value = false
      formError.value = error
    })
}

const onSubmit = (e) => {
  e.preventDefault()

  if (displayname.value !== props.profile?.displayname) {
    updateDisplayname()
  } else {
    toggleEditMode()
  }
}

// Function to save without toggling edit mode (for onboarding)
const saveForOnboarding = async () => {
  if (!props.autoEdit || displayname.value === props.profile?.displayname)
    return

  try {
    formBusy.value = true
    await storeProfile.updateProfile({ displayname: displayname.value })
    formBusy.value = false
  } catch (error) {
    formBusy.value = false
    formError.value = error
    throw error
  }
}

// Expose methods for parent component
defineExpose({
  saveForOnboarding,
  hasChanges: () => displayname.value !== props.profile?.displayname
})
</script>
